import { AttachFile, KeyboardArrowDown, Send } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  IconButton,
  InputBase,
  Typography,
  keyframes,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Copy } from 'lucide-react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { AskHauserMode } from '@/services/api/ask-hauser/api';

import HauserIcon from '@/assets/images/hauser-icon.svg?react';
import { POIGrid } from '@/components/dashboard/chat/POIGrid';
import { useDashboardLayout } from '@/context/DashboardLayoutProvider';
import { usePropertiesContext } from '@/context/PropertiesProvider';
import { useHauserEQResults } from '@/hooks/hauser-eq';
import { useAskHauser } from '@/hooks/useAskHauser';

/* ─── typing / streaming animations ─────────────────────── */

const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); }
  40% { transform: translateY(-4px); }
`;

const streamIndicatorPulse = keyframes`
  0%, 100% { opacity: 0.25; }
  50% { opacity: 1; }
`;

/* ─── constants ──────────────────────────────────────────── */

const hideSourceDollarValues = (content: string) => {
  const sourceMatch = content.match(/\bSources?\s*:/i);
  if (sourceMatch?.index === undefined) return content;

  const beforeSources = content.slice(0, sourceMatch.index);
  const sources = content
    .slice(sourceMatch.index)
    .replace(
      /\s*·?\s*(?:est\.?|estimate(?:d)?(?:\s+(?:cost|range))?)?\s*\$[\d,]+(?:\.\d+)?(?:\s*[–-]\s*\$[\d,]+(?:\.\d+)?)?/gi,
      '',
    )
    .replace(/\s+([|·])/g, ' $1')
    .replace(/([|·])\s+([|·])/g, '$2')
    .replace(/\s{2,}/g, ' ')
    .trim();

  return `${beforeSources}${sources}`;
};

const ResponseSources = ({ content }: { content: string }) => {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  const measureOverflow = useCallback(() => {
    const element = textRef.current;
    if (!element || expanded) return;
    setOverflows(element.scrollHeight > element.clientHeight + 1);
  }, [expanded]);

  useLayoutEffect(() => {
    if (expanded) return undefined;

    measureOverflow();
    const element = textRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measureOverflow);
    observer.observe(element);
    return () => observer.disconnect();
  }, [content, expanded, measureOverflow]);

  return (
    <Box
      component="span"
      data-testid="ask-hauser-response-sources"
      sx={{ display: 'block', color: 'var(--tx-muted)', fontSize: 10, lineHeight: 1.5 }}>
      <Box
        ref={textRef}
        component="span"
        sx={
          expanded
            ? { display: 'block' }
            : {
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
              }
        }>
        {content}
      </Box>
      {overflows && (
        <IconButton
          aria-label={expanded ? 'Collapse sources' : 'Expand sources'}
          onClick={() => setExpanded((current) => !current)}
          size="small"
          sx={{
            display: 'flex',
            width: 20,
            height: 18,
            mt: '2px',
            ml: 'auto',
            color: 'var(--tx-muted)',
            '&:hover': { backgroundColor: 'rgba(187,183,175,0.08)' },
          }}>
          <KeyboardArrowDown
            sx={{
              fontSize: 16,
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s ease',
            }}
          />
        </IconButton>
      )}
    </Box>
  );
};

const renderResponseContent = (content: string) => {
  const sanitizedContent = hideSourceDollarValues(content);
  const sourceMatch = sanitizedContent.match(/\bSources?\s*:/i);

  if (sourceMatch?.index === undefined) return sanitizedContent;

  return (
    <>
      {sanitizedContent.slice(0, sourceMatch.index)}
      <ResponseSources content={sanitizedContent.slice(sourceMatch.index)} />
    </>
  );
};

/* ─── types ──────────────────────────────────────────────── */

interface AskHauserTabProps {
  propertyId?: string;
  mode?: AskHauserMode;
  ownerNames?: string;
  onClearConversation?: () => void;
  hasMessages?: boolean;
}

/* ─── component ──────────────────────────────────────────── */

const AskHauserTab = ({ propertyId, mode, ownerNames }: AskHauserTabProps) => {
  const [inputValue, setInputValue] = useState('');
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageRefsMap = useRef<Map<string, HTMLDivElement>>(new Map());
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { pendingAskHauserQuestion, clearPendingAskHauserQuestion } = useDashboardLayout();
  const { selectedProperty } = usePropertiesContext();
  const { data: eqData } = useHauserEQResults(propertyId);

  // Build property context from HauserEQ scores for the chat system prompt.
  // Includes per-category subfactor breakdown + nearest points of interest so
  // the BE prompt-builder can surface area context (schools, walkability,
  // transit, etc.) inline without a tool round-trip.
  const propertyContext = eqData
    ? ({
        condition_score: eqData.condition_score,
        condition_subscores: eqData.condition_subscores,
        condition_category_scores: eqData.condition_category_scores,
        condition_kpis: eqData.condition_kpis,
        property_details: eqData.property_details,
        context_score: eqData.context_score,
        context_subscores: eqData.context_subscores,
        context_category_scores: eqData.context_category_scores,
        context_category_details: eqData.context_category_details,
        nearest_points_of_interest: eqData.nearest_points_of_interest,
      } as Record<string, unknown>)
    : undefined;

  const { messages, isTyping, sendMessage } = useAskHauser(propertyId, propertyContext, mode);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, scrollToBottom]);

  // Focus input when tab mounts (skip on mobile to avoid keyboard pushing UI off screen)
  useEffect(() => {
    if (!isMobile) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isMobile]);

  // Cancel an in-flight scheduled send only on genuine unmount — not on every
  // re-run of the effect below. clearPendingAskHauserQuestion() itself causes
  // that effect to re-run (pendingAskHauserQuestion is a dependency) well
  // before the 150ms delay elapses; if cancellation were tied to that effect's
  // own cleanup, this unmount-only split wouldn't be needed, but it also
  // wouldn't wrongly cancel a send we ourselves triggered by clearing the flag.
  const scheduledSendTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handledPendingQuestionRef = useRef<object | null>(null);
  useEffect(
    () => () => {
      if (scheduledSendTimeoutRef.current) clearTimeout(scheduledSendTimeoutRef.current);
    },
    [],
  );

  // Auto-send or scroll-to-existing for questions injected via openAskHauserWithQuestion.
  // If the same question was already answered after the last property update, scroll to it.
  // If the property has been updated since, or no prior answer exists, re-send.
  useEffect(() => {
    if (!pendingAskHauserQuestion) return;
    if (handledPendingQuestionRef.current === pendingAskHauserQuestion) return;
    handledPendingQuestionRef.current = pendingAskHauserQuestion;
    const { prompt, displayText } = pendingAskHauserQuestion;
    const label = displayText ?? prompt;
    clearPendingAskHauserQuestion();

    const propertyUpdatedAt = selectedProperty?.property.updatedAt ?? 0;
    const existingUserMsg = [...messages]
      .reverse()
      .find(
        (message) =>
          message.role === 'user' && (message.content === label || message.rawContent === prompt),
      );
    const isStillFresh =
      existingUserMsg?.createdAt !== undefined && existingUserMsg.createdAt > propertyUpdatedAt;

    if (isStillFresh && existingUserMsg) {
      const existingIdx = messages.findIndex((m) => m.id === existingUserMsg.id);
      const existingResponse = messages.slice(existingIdx + 1).find((m) => m.role === 'assistant');
      const scrollTarget = existingResponse ?? existingUserMsg;

      // The tab and drawer may still be mounting when a question is opened from
      // a dashboard card. Resolve the ref after the drawer transition begins;
      // if the old answer still cannot be surfaced, send the question instead
      // of silently swallowing the click.
      if (scheduledSendTimeoutRef.current) return;
      scheduledSendTimeoutRef.current = setTimeout(() => {
        scheduledSendTimeoutRef.current = null;
        const el = messageRefsMap.current.get(scrollTarget.id);
        if (el?.isConnected) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        sendMessage(prompt, displayText);
      }, 300);
      return;
    }

    // Send immediately. The request-object guard above prevents StrictMode
    // from double-sending while avoiding a drawer/tab re-render cancelling
    // the CTA handoff before the mutation starts.
    sendMessage(prompt, displayText);
  }, [
    pendingAskHauserQuestion,
    clearPendingAskHauserQuestion,
    sendMessage,
    messages,
    selectedProperty,
  ]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const handleSend = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      setInputValue('');
      sendMessage(trimmed);
    },
    [sendMessage],
  );

  const handleCopyMessage = useCallback(async (messageId: string, content: string) => {
    const text = hideSourceDollarValues(content);
    await navigator.clipboard.writeText(text);
    setCopiedMessageId(messageId);
    window.setTimeout(
      () => setCopiedMessageId((current) => (current === messageId ? null : current)),
      1200,
    );
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend(inputValue);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {/* ─── Disclaimer ─────────────────────────────────── */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            mx: '20px',
            mt: '12px',
            // Persistent gap below the disclaimer that survives scrolling.
            // The messages container's `pt: 16px` scrolls away with its
            // content, so without `mb` here chat messages can sit flush
            // against the disclaimer once the user scrolls.
            mb: '12px',
            px: '12px',
            py: '8px',
            borderRadius: '10px',
            backgroundColor: 'var(--tx-fill)',
            flexShrink: 0,
          }}>
          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'var(--tx-faint)' }} />
          <Typography
            sx={{
              fontSize: 11,
              color: 'var(--tx-muted)',
              lineHeight: 1.3,
            }}>
            Property data only — no financial, legal, or investment advice.
          </Typography>
        </Box>

        {/* ─── Messages / Welcome area ────────────────────── */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: '20px',
            pt: '16px',
            pb: '8px',
            display: 'flex',
            flexDirection: 'column',
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'var(--tx-fill-3)',
              borderRadius: 2,
            },
          }}>
          {/* Welcome greeting — shown when no messages */}
          {messages.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                pt: 4,
                pb: 2,
              }}>
              <Typography
                sx={{
                  color: 'var(--tx-text)',
                  fontSize: 22,
                  fontWeight: 600,
                  lineHeight: 1.3,
                  mb: 1,
                }}>
                {getGreeting()}
                {ownerNames ? `, ${ownerNames}` : ''}
              </Typography>
              <Typography
                sx={{
                  color: 'var(--tx-muted)',
                  fontSize: 13,
                  lineHeight: 1.5,
                  maxWidth: 300,
                }}>
                Ask me anything about this property.
              </Typography>
            </Box>
          )}

          {/* Chat messages */}
          {messages.map((msg, i) => (
            <Box
              key={msg.id}
              // a turn change (you → Hauser, Hauser → you) gets 28px; consecutive messages from one side stay at 12
              sx={{ mt: i > 0 && messages[i - 1].role !== msg.role ? '28px' : i > 0 ? '12px' : 0 }}
              ref={(el) => {
                if (el) messageRefsMap.current.set(msg.id, el as HTMLDivElement);
                else messageRefsMap.current.delete(msg.id);
              }}>
              {msg.role === 'user' ? (
                /* ─── User message bubble ──── */
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Box
                    sx={{
                      backgroundColor: 'var(--tx-fill-2)',
                      color: 'var(--tx-text)',
                      borderRadius: '16px 16px 4px 16px',
                      px: '14px',
                      py: '10px',
                      maxWidth: '85%',
                      fontSize: 13,
                      lineHeight: 1.5,
                      fontWeight: 400,
                    }}>
                    {hideSourceDollarValues(msg.content)}
                  </Box>
                </Box>
              ) : (
                /* ─── AI message ──── */
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: '12px' }}>
                  {/* Avatar */}
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      mt: '1px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--tx-lime)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      alignSelf: 'flex-start',
                    }}>
                    <HauserIcon style={{ width: 20, height: 20, color: '#191919' }} />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      maxWidth: '85%',
                    }}>
                    {/* Bubble */}
                    <Box
                      sx={{
                        backgroundColor: 'var(--tx-glass-panel)',
                        backgroundImage: 'var(--tx-surface-light)',
                        boxShadow: 'var(--tx-shadow)',
                        color: 'var(--tx-text)',
                        borderRadius: '16px 16px 16px 4px',
                        px: '14px',
                        py: '10px',
                        fontSize: 13,
                        lineHeight: 1.7,
                        fontWeight: 400,
                        '& strong': { fontWeight: 600 },
                        whiteSpace: 'pre-wrap',
                      }}>
                      {renderResponseContent(msg.content)}
                      {msg.isStreaming && (
                        <Box
                          component="span"
                          data-testid="ask-hauser-streaming-caret"
                          aria-hidden="true"
                          sx={{
                            display: 'inline-block',
                            width: 10,
                            height: 5,
                            borderRadius: '999px',
                            backgroundColor: 'var(--tx-lime)',
                            ml: '4px',
                            verticalAlign: 'middle',
                            animation: `${streamIndicatorPulse} 900ms ease-in-out infinite`,
                          }}
                        />
                      )}
                      {/* Structured tool result cards rendered below the
                          text — currently only get_pois. Gated on
                          !isStreaming so cards appear after the written
                          context settles instead of outpacing it (tool
                          results often arrive before/during the stream). */}
                      {!msg.isStreaming &&
                        msg.toolResults
                          ?.filter((tr) => tr.toolName === 'get_pois')
                          .map((tr, i) => {
                            const pois = Array.isArray(tr.result)
                              ? (tr.result as Parameters<typeof POIGrid>[0]['pois'])
                              : [];
                            if (pois.length === 0) return null;
                            return <POIGrid key={`pois-${tr.index ?? i}`} pois={pois} />;
                          })}
                    </Box>
                    {!msg.isStreaming && (
                      <IconButton
                        aria-label="Copy response"
                        title={copiedMessageId === msg.id ? 'Copied' : 'Copy response'}
                        onClick={() => handleCopyMessage(msg.id, msg.content)}
                        sx={{
                          mt: '4px',
                          ml: '2px',
                          width: 28,
                          height: 28,
                          color: copiedMessageId === msg.id ? 'var(--tx-lime-ink)' : 'var(--tx-faint)',
                          '&:hover': {
                            color: 'var(--tx-text)',
                            backgroundColor: 'var(--tx-fill)',
                          },
                        }}>
                        <Copy size={18} strokeWidth={2} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          ))}

          {/* Typing indicator — only shown while waiting for first chunk */}
          {isTyping && !messages.some((m) => m.role === 'assistant' && m.isStreaming) && (
            <Box sx={{ mb: 1.5, display: 'flex', flexDirection: 'row', gap: '12px' }}>
              <Box
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  backgroundColor: 'var(--tx-lime)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  alignSelf: 'flex-start',
                }}>
                <HauserIcon style={{ width: 20, height: 20, color: '#191919' }} />
              </Box>
              <Box
                sx={{
                  display: 'inline-flex',
                  gap: '4px',
                  backgroundColor: 'var(--tx-glass-panel)',
                  backgroundImage: 'var(--tx-surface-light)',
                  boxShadow: 'var(--tx-shadow)',
                  borderRadius: '16px 16px 16px 4px',
                  px: '14px',
                  py: '12px',
                  alignItems: 'center',
                }}>
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: 'var(--tx-fill-3)',
                      animation: `${bounce} 1.2s ease-in-out infinite`,
                      animationDelay: `${i * 0.15}s`,
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <div ref={messagesEndRef} />
        </Box>

        {/* ─── Input area ─────────────────────────────────── */}
        <Box sx={{ px: '20px', pt: '10px', pb: '20px', flexShrink: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              backgroundColor: 'rgba(var(--tx-ink-rgb), 0.05)',
              borderRadius: '12px',
              pl: '4px',
              pr: '14px',
              height: 54,
            }}>
            <IconButton
              component="label"
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
              }}>
              <AttachFile
                sx={{
                  fontSize: 18,
                  color: 'var(--tx-faint)',
                  transform: 'rotate(45deg)',
                }}
              />
              <input type="file" hidden />
            </IconButton>
            <InputBase
              inputRef={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about this property..."
              disabled={isTyping}
              data-testid="ask-hauser-input"
              sx={{
                flex: 1,
                '& .MuiInputBase-input': {
                  fontSize: '13px !important',
                  fontFamily: 'Manrope, sans-serif !important',
                  color: 'var(--tx-text)',
                },
                '& input::placeholder': {
                  color: 'var(--tx-faint)',
                  opacity: 1,
                },
              }}
            />
            <IconButton
              onClick={() => handleSend(inputValue)}
              disabled={!inputValue.trim() || isTyping}
              data-testid="ask-hauser-send-btn"
              sx={{
                width: 32,
                height: 32,
                backgroundColor:
                  inputValue.trim() && !isTyping ? '#C0FF02' : 'rgba(255,255,255,0.08)',
                '&:hover': {
                  backgroundColor:
                    inputValue.trim() && !isTyping ? '#D4FF4D' : 'rgba(255,255,255,0.12)',
                },
                '&.Mui-disabled': {
                  backgroundColor: 'var(--tx-fill-2)',
                },
                transition: 'background-color 0.15s ease',
              }}>
              <Send
                sx={{
                  fontSize: 16,
                  color: inputValue.trim() && !isTyping ? 'var(--tx-text)' : 'var(--tx-faint)',
                  transform: 'rotate(-45deg)',
                }}
              />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AskHauserTab;
